import { Injectable, inject, signal, WritableSignal, effect, untracked } from '@angular/core';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { gql } from 'apollo-angular';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { MatrixRequest, CachedRequest } from '../../models';
import { isEmpty } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { DatiqFileDownloadService } from '@dat/datiq-components-pkg/file-download';
import { EquipmentTypes } from '../../models';

@Injectable({
    providedIn: 'root',
})
export class IqStateRegionMatrixStateService {
    private _apollo: DatiqGraphqlApolloService = inject(DatiqGraphqlApolloService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _file: DatiqFileDownloadService = inject(DatiqFileDownloadService);

    downloadMatrixLink: WritableSignal<string> = signal('');
    equipmentType: WritableSignal<string> = signal(EquipmentTypes.Van);
    fuelSurcharge: WritableSignal<number> = signal(0);

    cachedRequests: CachedRequest[] = [];

    constructor() {
        this._bus.registerMap({
            'type:fetch-matrix-file': {
                callback: ({ matrixRequest }: { matrixRequest: MatrixRequest }) => {
                    if (isEmpty(matrixRequest)) return;

                    const cachedRequest: CachedRequest = this.cachedRequests.find(
                        (item) => JSON.stringify(item.request) === JSON.stringify(matrixRequest)
                    );

                    if (cachedRequest) {
                        this.downloadMatrixLink.set(cachedRequest.link);
                        this._file.triggerDownloadThroughUrl(cachedRequest.link, 'state-matrix.csv');
                        return;
                    }

                    this.fetchMatrixFile(matrixRequest).then((result: any) => {
                        const linkCacheItem: CachedRequest = {
                            request: matrixRequest,
                            link: result.data.getStateMatrixFile.url,
                        }
                        this.cachedRequests.push(linkCacheItem);
                        this.downloadMatrixLink.set(linkCacheItem.link);
                        this._file.triggerDownloadThroughUrl(linkCacheItem.link, 'state-matrix.csv');
                    });
                }
            },
            'setLandingEquipmentType': {
                callback: ({ equipmentType }: { equipmentType: string }) => {
                    if (isEmpty(equipmentType)) return;

                    this.fetchNtlHistoricalRateFuel(equipmentType).then((result: any) => {
                        if (result.data.national.rates.mile.length) {
                            const fuelSurcharge = result.data.national.rates.mile.pop().average.toFixed(2);

                            this.fuelSurcharge.set(fuelSurcharge);
                        }
                    });
                },
            }
        });
    }

    private fetchMatrixFile(matrixRequest: MatrixRequest): Promise<any> {   
        return firstValueFrom(this._apollo.getApollo()
            .query({
            query: gql`
                query getStateMatrixFile(
                $equipmentType: STATEMATRIX_EquipmentCategory,
                $rateType: STATEMATRIX_RateType,
                $geography: STATEMATRIX_GeographyType,
                $timeframe: STATEMATRIX_Timeframe,
                $fillEmptyRates: Boolean,
                $includeFuelSurcharge: Boolean
                ) {
                getStateMatrixFile(
                    query: {
                    equipmentType: $equipmentType,
                    rateType: $rateType,
                    geography: $geography,
                    timeframe: $timeframe,
                    fillEmptyRates: $fillEmptyRates,
                    includeFuelSurcharge: $includeFuelSurcharge
                    }
                ) {
                    url
                    expiration
                }
                }
            `,
            variables: {
                equipmentType: matrixRequest.equipmentType,
                rateType: matrixRequest.rateType,
                geography: matrixRequest.geoType,
                timeframe: 'THIRTY_DAY',
                fillEmptyRates: false,
                includeFuelSurcharge: true
            }
            })
        );
    }

    private fetchNtlHistoricalRateFuel(equipmentType: string): Promise<any> {
            return firstValueFrom(
                this._apollo.getApollo().query({
                    query: gql`
                        query (
                            $query: RATESNATIONAL_SearchCriteriaInputRatesNational
                        ) {
                            national: getRatesNational(query: $query) {
                                rates {
                                    mile {
                                        date
                                        average
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        query: {
                            equipmentCategory: equipmentType,
                            country: 'US',
                            period: 'Week',
                            rateType: 'Fuel',
                        },
                    },
                }),
            )
        }
}